/** Calendar dates are timezone-free values. Date conversion belongs at UI boundaries. */
declare const calendarDateBrand: unique symbol;
export type CalendarDate = string & { readonly [calendarDateBrand]: true };

export function parseCalendarDate(input: string): CalendarDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > days[month - 1])
    return null;
  return input as CalendarDate;
}

/** Used only at an input boundary whose explicit text format is MM/DD/YYYY. */
export function parseUsCalendarDate(input: string): CalendarDate | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(input);
  return match
    ? parseCalendarDate(
        `${match[3]}-${match[1].padStart(2, "0")}-${match[2].padStart(2, "0")}`,
      )
    : null;
}

/** MUI/date-fns adapter boundary: inspect the displayed local date. */
export function fromPickerDate(value: Date | null): CalendarDate | null {
  if (!value || Number.isNaN(value.getTime())) return null;
  return parseCalendarDate(
    `${String(value.getFullYear()).padStart(4, "0")}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`,
  );
}

/** Keep Date construction inside the picker adapter, never in calendar-date consumers. */
export function toPickerDate(value: CalendarDate | null): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  // setFullYear avoids the new Date(1, ...) -> 1901 special case.
  const result = new Date(0);
  result.setFullYear(year, month - 1, day);
  result.setHours(0, 0, 0, 0);
  // Some zones have skipped an entire civil day. Never silently return a different one.
  return fromPickerDate(result) === value ? result : null;
}

export function displayUsCalendarDate(value: CalendarDate): string {
  const [year, month, day] = value.split("-").map(Number);
  return `${month}/${day}/${year}`;
}

/** Extract an instant's calendar date using an explicit timezone policy. */
export function calendarDateInTimeZone(
  instant: Date,
  timeZone: string,
): CalendarDate {
  const parts = new Intl.DateTimeFormat("en-US-u-ca-gregory-nu-latn", {
    timeZone,
    era: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  if (get("era") !== "AD") throw new RangeError("Unsupported calendar date");
  const result = parseCalendarDate(
    `${get("year").padStart(4, "0")}-${get("month")}-${get("day")}`,
  );
  if (!result) throw new RangeError("Unsupported calendar date");
  return result;
}

/** Caller chooses the practice timezone or an explicitly UTC policy. */
export const todayCalendarDate = (
  timeZone: string,
  now = new Date(),
): CalendarDate => calendarDateInTimeZone(now, timeZone);

/** UTC-midnight protocol adapter, for contracts such as RRULE UNTIL. Not a picker value. */
export const toUtcMidnight = (value: CalendarDate): Date =>
  new Date(`${value}T00:00:00.000Z`);

/** Add calendar days without crossing local DST or skipped-day offsets. */
export function addCalendarDays(
  value: CalendarDate,
  days: number,
): CalendarDate {
  if (!Number.isInteger(days))
    throw new RangeError("Calendar days must be an integer");
  const date = toUtcMidnight(value);
  date.setUTCDate(date.getUTCDate() + days);
  return calendarDateInTimeZone(date, "UTC");
}

/** Calendar comparison preserves the existing birthday/month-day policy, including February 29. */
export function ageOnCalendarDate(
  birth: CalendarDate,
  on: CalendarDate,
): number {
  const yearDifference = Number(on.slice(0, 4)) - Number(birth.slice(0, 4));
  return yearDifference - (on.slice(5) < birth.slice(5) ? 1 : 0);
}

/** Format a known Date using local calendar components. Invalid Dates throw. */
export function localCalendarDate(value: Date): CalendarDate {
  const result = fromPickerDate(value);
  if (!result) throw new RangeError("Unsupported calendar date");
  return result;
}

/** Browser-local today, for inputs and screens whose existing calendar is local. */
export const localTodayCalendarDate = (now = new Date()): CalendarDate =>
  localCalendarDate(now);

/** Preserve locale-specific date display without applying the browser's timezone. */
export function displayCalendarDate(
  value: CalendarDate,
  locales?: Intl.LocalesArgument,
  options: Omit<Intl.DateTimeFormatOptions, "timeZone"> = {},
): string {
  return toUtcMidnight(value).toLocaleDateString(locales, {
    ...options,
    timeZone: "UTC",
  });
}

/** Add whole calendar years, rolling February 29 to March 1 in non-leap years. */
export function addCalendarYears(value: CalendarDate, years: number): CalendarDate {
  if (!Number.isInteger(years)) throw new RangeError("Calendar years must be an integer");
  const date = toUtcMidnight(value);
  date.setUTCFullYear(date.getUTCFullYear() + years);
  return calendarDateInTimeZone(date, "UTC");
}

/** Signed calendar-day distance; independent of local DST and skipped civil days. */
export function differenceInCalendarDays(end: CalendarDate, start: CalendarDate): number {
  return (toUtcMidnight(end).getTime() - toUtcMidnight(start).getTime()) / 86_400_000;
}
